/**
 * ════════════════════════════════════════════════════════════
 *  ECHO CONFIGURATION SCHEMAS
 *  Zod runtime validation for Organization.echoConfig.
 *
 *  Drift guard: the `satisfies z.ZodType<OrganizationEchoConfig>`
 *  assertion at the bottom makes the schema structurally match
 *  the manually-declared interface. A field added to the interface
 *  that is missing here — or a field added here that contradicts the
 *  interface — fails the package typecheck.
 * ════════════════════════════════════════════════════════════
 */

import { z } from "zod";

import type { OrganizationEchoConfig } from "./config.types";

// ─── Enums / primitives ──────────────────────────────────────

const EchoStorageTypeSchema = z.enum(["s3", "gcs", "azure"]);
const EchoDeletionPolicySchema = z.enum(["never", "after_archive", "after_retention"]);
const EchoFailureModeSchema = z.enum(["FAIL_REQUEST", "CONTINUE_AND_ALERT"]);
const EchoNormalModeSchema = z.enum(["BEST_EFFORT", "GUARANTEED"]);
const EchoRedactionStrategySchema = z.enum(["mask", "hash", "remove"]);
const EchoExportFrequencySchema = z.enum(["hourly", "daily", "weekly", "monthly"]);
const EchoExportFormatSchema = z.enum(["json", "csv", "parquet"]);
const EchoCompressionSchema = z.enum(["none", "gzip", "zstd"]);
const EchoStreamingTypeSchema = z.enum(["webhook", "kafka", "kinesis", "pubsub"]);
const EchoAlertSeveritySchema = z.enum(["warning", "error", "critical"]);
const EchoQueuePrioritySchema = z.enum(["low", "normal", "high"]);
const EchoPartitionStrategySchema = z.enum(["monthly", "weekly", "daily"]);
const EchoComplianceStandardSchema = z.enum(["SOC2", "GDPR", "HIPAA", "PCI-DSS", "ISO27001"]);
const EchoSignatureAlgorithmSchema = z.enum(["HMAC-SHA256", "RSA-SHA256"]);
const EchoReportFrequencySchema = z.enum(["weekly", "monthly", "quarterly"]);
const EchoSIEMProviderSchema = z.enum(["splunk", "datadog", "elastic", "custom"]);

// ─── Event filtering ─────────────────────────────────────────

const EchoEventFilteringSchema = z.object({
  enabled: z.array(z.string()),
  disabled: z.array(z.string()),
  sampling: z.record(z.string(), z.number().min(0).max(1))
});

// ─── Retention ───────────────────────────────────────────────

const EchoArchiveDestinationSchema = z.object({
  type: EchoStorageTypeSchema,
  bucket: z.string(),
  path: z.string(),
  region: z.string().optional(),
  credentials: z
    .object({
      accessKeyId: z.string().optional(),
      secretAccessKey: z.string().optional()
    })
    .optional()
});

const EchoRetentionSchema = z.object({
  daysInDatabase: z.number().int().min(1).max(3650),
  autoArchive: z.boolean(),
  archiveAfterDays: z.number().int().min(1),
  deletionPolicy: EchoDeletionPolicySchema,
  archiveDestination: EchoArchiveDestinationSchema.optional()
});

// ─── Failure handling ────────────────────────────────────────

const EchoFailureHandlingSchema = z.object({
  critical: EchoFailureModeSchema,
  normal: EchoNormalModeSchema,
  retries: z.object({
    maxAttempts: z.number().int().min(1),
    backoffMs: z.number().int().min(0)
  }),
  failedQueue: z.object({
    enabled: z.boolean(),
    maxSize: z.number().int().min(0),
    alertThreshold: z.number().int().min(0)
  }),
  fileSpillover: z.object({
    enabled: z.boolean(),
    path: z.string(),
    maxSizeGB: z.number().min(0)
  })
});

// ─── Privacy / PII ───────────────────────────────────────────

const EchoPrivacySchema = z.object({
  redactSensitiveFields: z.boolean(),
  sensitiveFieldPatterns: z.array(z.string()),
  redactionStrategy: EchoRedactionStrategySchema,
  piiPatterns: z.object({
    email: z.boolean(),
    phone: z.boolean(),
    ssn: z.boolean(),
    creditCard: z.boolean(),
    custom: z.array(z.string())
  }),
  anonymizeIP: z.boolean(),
  trackGeolocation: z.boolean()
});

// ─── Export & streaming ──────────────────────────────────────

const EchoScheduledExportSchema = z.object({
  enabled: z.boolean(),
  frequency: EchoExportFrequencySchema,
  time: z.string().optional(),
  dayOfWeek: z.number().int().min(0).max(6).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  destination: z.object({
    type: z.union([EchoStorageTypeSchema, z.literal("webhook")]),
    config: z.record(z.string(), z.unknown())
  }),
  format: EchoExportFormatSchema,
  compression: EchoCompressionSchema
});

const EchoStreamingDestinationSchema = z.object({
  type: EchoStreamingTypeSchema,
  url: z.string(),
  filters: z
    .object({
      eventTypes: z.array(z.string()).optional(),
      severity: z.array(z.string()).optional()
    })
    .optional(),
  batchSize: z.number().int().optional(),
  batchTimeoutMs: z.number().int().optional()
});

const EchoExportConfigSchema = z.object({
  allowManual: z.boolean(),
  maxRecordsPerExport: z.number().int().min(1),
  scheduled: EchoScheduledExportSchema,
  streaming: z.object({
    enabled: z.boolean(),
    destinations: z.array(EchoStreamingDestinationSchema)
  })
});

// ─── Alerts ──────────────────────────────────────────────────

const EchoAlertsSchema = z.object({
  enabled: z.boolean(),
  onFailure: z.object({
    enabled: z.boolean(),
    threshold: z.number().int().min(0),
    cooldownMinutes: z.number().int().min(0)
  }),
  onAnomaly: z.object({
    enabled: z.boolean(),
    rules: z.array(
      z.object({
        name: z.string(),
        condition: z.string(),
        severity: EchoAlertSeveritySchema
      })
    )
  }),
  channels: z.object({
    email: z
      .object({
        enabled: z.boolean(),
        recipients: z.array(z.string())
      })
      .optional(),
    slack: z
      .object({
        enabled: z.boolean(),
        webhookUrl: z.string(),
        channel: z.string().optional()
      })
      .optional(),
    webhook: z
      .object({
        enabled: z.boolean(),
        url: z.string(),
        headers: z.record(z.string(), z.string()).optional()
      })
      .optional(),
    pagerduty: z
      .object({
        enabled: z.boolean(),
        deploymentKey: z.string()
      })
      .optional()
  })
});

// ─── Performance ─────────────────────────────────────────────

const EchoPerformanceSchema = z.object({
  batching: z.object({
    enabled: z.boolean(),
    maxBatchSize: z.number().int().min(1),
    maxBatchTimeMs: z.number().int().min(0)
  }),
  queuePriority: EchoQueuePrioritySchema,
  usePartitioning: z.boolean(),
  partitionStrategy: EchoPartitionStrategySchema,
  additionalIndexes: z.array(z.string())
});

// ─── Compliance ──────────────────────────────────────────────

const EchoComplianceSchema = z.object({
  standards: z.array(EchoComplianceStandardSchema),
  immutable: z.boolean(),
  signatures: z.object({
    enabled: z.boolean(),
    algorithm: EchoSignatureAlgorithmSchema,
    secretKey: z.string().optional()
  }),
  metaLogging: z.boolean(),
  reports: z.object({
    enabled: z.boolean(),
    frequency: EchoReportFrequencySchema,
    recipients: z.array(z.string())
  })
});

// ─── Advanced ────────────────────────────────────────────────

const EchoAdvancedSchema = z.object({
  siem: z.object({
    enabled: z.boolean(),
    provider: EchoSIEMProviderSchema,
    endpoint: z.string().optional(),
    apiKey: z.string().optional()
  }),
  analytics: z.object({
    enabled: z.boolean(),
    dashboards: z.boolean(),
    anomalyDetection: z.boolean(),
    mlModels: z.array(z.string())
  }),
  queryApi: z.object({
    enabled: z.boolean(),
    maxResultsPerQuery: z.number().int().min(1),
    rateLimitPerMin: z.number().int().min(0)
  }),
  criticalEventPatterns: z.array(z.string())
});

// ─── Complete configuration ──────────────────────────────────

/**
 * Full Zod schema for `OrganizationEchoConfig`.
 *
 * Use this at every boundary where an echoConfig value is materialized from
 * untyped JSON (DB reads, HTTP bodies, queue payloads). Never cast — always
 * `safeParse()` or `parse()` so schema drift surfaces at the boundary, not
 * deep in the interceptor.
 */
export const OrganizationEchoConfigSchema = z.object({
  enabled: z.boolean(),
  version: z.string(),
  events: EchoEventFilteringSchema,
  retention: EchoRetentionSchema,
  failureMode: EchoFailureHandlingSchema,
  privacy: EchoPrivacySchema,
  export: EchoExportConfigSchema,
  alerts: EchoAlertsSchema,
  performance: EchoPerformanceSchema,
  compliance: EchoComplianceSchema,
  advanced: EchoAdvancedSchema
}) satisfies z.ZodType<OrganizationEchoConfig>;

/**
 * Recursively deep-partials a schema, replacing Zod v3's removed `.deepPartial()`.
 * Used at runtime to build the PATCH schema below, so it must match v3's behaviour:
 * make every field optional at every depth, unwrapping the wrappers v3 also traversed
 * (ZodOptional / ZodNullable) and recursing into array elements — not just plain objects.
 */
type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;

const deepPartialify = (schema: z.ZodTypeAny): z.ZodTypeAny => {
  if (schema instanceof z.ZodObject) {
    const shape = schema.shape;
    const partialShape: Record<string, z.ZodTypeAny> = {};
    for (const key of Object.keys(shape)) {
      partialShape[key] = deepPartialify(shape[key] as z.ZodTypeAny).optional();
    }
    return z.object(partialShape);
  }
  if (schema instanceof z.ZodArray) {
    return z.array(deepPartialify(schema.element as z.ZodTypeAny));
  }
  if (schema instanceof z.ZodOptional) {
    return deepPartialify(schema.unwrap() as z.ZodTypeAny).optional();
  }
  if (schema instanceof z.ZodNullable) {
    return deepPartialify(schema.unwrap() as z.ZodTypeAny).nullable();
  }
  return schema;
};

/**
 * Deep-partial schema for PATCH-style updates. Every field at every depth is
 * optional so clients can send only the subsection they want to change.
 * The server MUST merge this patch into the current config before persisting
 * (see `echo.controller.ts`).
 *
 * The result type is asserted because the recursive helper erases the precise
 * per-field shape; `z.infer` of the asserted schema stays deeply optional.
 */
export const OrganizationEchoConfigPatchSchema = deepPartialify(
  OrganizationEchoConfigSchema
) as unknown as z.ZodType<DeepPartial<OrganizationEchoConfig>>;

export type OrganizationEchoConfigPatch = z.infer<typeof OrganizationEchoConfigPatchSchema>;
